import { useMemo } from 'react';

import _ from 'lodash';
import { usePlmId, addPlmPrefix } from '../utilities/PgmUtilities';
import { apiLatch } from '../utilities/PerformanceUtilities';

export const contentTypeHeader = 'Content-type';
export const contentTypeJSON = 'application/json';
export const utf8 = 'charset=UTF-8';
export const contentTypePlainText = 'text/plain';

export const HTTP_STATUS_200 = 200;
export const HTTP_STATUS_400 = 400;
export const HTTP_STATUS_401 = 401;
export const HTTP_STATUS_403 = 403;
export const HTTP_STATUS_500 = 500;

class HttpError extends Error {
    constructor(response, responseBody) {
        super(response.statusText);
        this.response = response;
        this.responseBody = responseBody;
    }

    getStatus() {
        return this.response.status;
    }
}

export const GLOBAL_RESPONSE_INTERCEPTORS = {};

export function sendRequest(baseUrl, requestUrl, options, errorHandler = handleResponseError) {
    const done = apiLatch(baseUrl);

    // eslint-disable-next-line react-portal/no-global-fetch
    return fetch(requestUrl, options)
        .then((response) => handleResponse(response).then((responseBody) => handleInterceptors(responseBody, response)))
        .catch(errorHandler)
        .finally(done);
}

function isJson(contentType) {
    return contentType && contentType.includes(contentTypeJSON);
}

function isText(contentType) {
    return contentType && contentType.includes(contentTypePlainText);
}

async function getResponseBody(response) {
    const contentType = response?.headers?.get(contentTypeHeader);

    if (isJson(contentType)) {
        const responseText = await response.text();

        // Our API isn't consistent. We have calls that will have "200 OK" and "Content-Type: application/json"
        // but not return a body. Trying to parse that as JSON throws a syntax error and makes it seem like
        // the call itself failed. This makes sure we actually have a text response before parsing it.
        if (responseText) {
            return JSON.parse(responseText);
        }

        return null;
    }

    if (isText(contentType)) {
        return response.text();
    }

    return response.body;
}

/**
 * Handle HTTP error response
 * @param {HttpError} responseError
 * @returns {*|Promise<any>}
 */
export function handleResponseError(responseError) {
    // handle unauthorized HTTP request
    if (responseError?.response?.status === HTTP_STATUS_401) {
        // eslint-disable-next-line react-portal/no-window-location-assign
        window.location.assign('/logout');
        return;
    }

    throw responseError;
}

/**
 * Fetch doesn't throw an error, so we need to check the status
 * of the response here, that would change if we swapped third parties.
 * @param {Object} response
 * @returns {*|Promise<any>}
 */
export function handleResponse(response) {
    if (!response.ok) {
        return getResponseBody(response).then((responseBody) => {
            throw new HttpError(response, responseBody);
        });
    }

    return getResponseBody(response);
}

function handleInterceptors(responseBody, response) {
    _.values(GLOBAL_RESPONSE_INTERCEPTORS).forEach((callback) => {
        if (typeof callback === 'function') {
            callback(responseBody, response);
        }
    });

    return responseBody;
}

export function cleanUrl(url) {
    let cleaned = url.replace(/\/\//g, '/');
    cleaned = cleaned.replace(/\/$/gim, '');

    return cleaned;
}

export function useRequestService() {
    const plmId = usePlmId();
    return useMemo(() => RequestServiceFactory(plmId), [plmId]);
}

function RequestServiceFactory(plmId) {
    function get(url, errorHandler) {
        return sendRequest(
            url,
            addPlmPrefix(cleanUrl(url), plmId),
            {
                method: 'GET',
            },
            errorHandler
        );
    }

    function post(url, body, errorHandler) {
        return sendRequest(
            url,
            addPlmPrefix(cleanUrl(url), plmId),
            _.extend(
                {},
                {
                    method: 'POST',
                    headers: {
                        [contentTypeHeader]: `${contentTypeJSON}; ${utf8}`,
                    },
                    body: JSON.stringify(body),
                }
            ),
            errorHandler
        );
    }

    return {
        get,
        post,
    };
}

export { RequestServiceFactory };
